/**
 * File: TodayPage.tsx
 * Path: apps/admin/src/routes/today/TodayPage.tsx
 * Description: The CRM home (Liam, admin UI audit C2, 4 Nov 2026): Today is for
 * the day's work, the accounting Overview is for the books. The title comes
 * first, then the Business / Website tabs (when the role has both). Money is
 * one line that opens Overview. Then the two boxes that matter (open pipeline,
 * hours this week), the invoices Ready to send and the Reminders, the Do today
 * list beside the open pipeline by stage, and the quotes and clients for the
 * dates picked. The date picker sits on those last cards, the only ones it
 * changes. The one main button is "New".
 */

import React from "react";
import { ChevronDown, Plus } from "lucide-react";
import { DEFAULT_YEAR_END_MONTH } from "@idk/config";
import { Button, DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@idk/ui";
import { PageHeader } from "@/modules/layout/components/PageHeader";
import { StatStrip } from "@/modules/layout/components/StatStrip";
import { Box, Figure, SectionLabel, notice } from "@/shared/components/BooksPrimitive";
import { AttentionList } from "@/shared/components/overview/AttentionList";
import { BarList } from "@/shared/components/overview/BarList";
import { DateRangeSelect } from "@/shared/components/overview/DateRangeSelect";
import { AddLeadDialog } from "@/shared/components/AddLeadDialog";
import { ReadyToSendCard } from "@/shared/components/ReadyToSendCard";
import { RemindersCard } from "@/features/today/components/RemindersCard";
import { TodayMoneyLine } from "@/features/today/components/TodayMoneyLine";
import { useNewMenuController } from "@/features/today/hooks/useNewMenuController";
import { useTodayBodyController } from "@/features/today/hooks/useTodayBodyController";
import { useTodayPageController } from "@/features/today/hooks/useTodayPageController";
import { figureChips } from "@/features/today/utils/todayFiguresUtils";
import { BOOKS_START, hoursText, type DayRange, type TodayOverview } from "@/features/today/utils/todayOverviewUtils";

const empty = "border border-dashed border-border/60 p-gutter-md text-sm text-muted-foreground";

const TodayFigures: React.FC<{ overview: TodayOverview }> = ({ overview }) => {
  const f = overview.figures;
  const chips = figureChips(f);
  return (
    <StatStrip as="dl" variant="cards" label="Key figures" desktop="sm:grid-cols-2">
      <Figure
        label="Open pipeline"
        cents={f.openValueCents}
        explain="Deals still open, added up."
        chip={chips.pipeline}
      />
      <Figure
        label="Hours this week"
        value={hoursText(f.weekMinutes)}
        explain="Time logged on client work since Monday."
        chip={chips.hours}
      />
    </StatStrip>
  );
};

const TodayBody: React.FC<{ range: DayRange; today: string; picker: React.ReactNode }> = ({ range, today, picker }) => {
  const { failed, overview } = useTodayBodyController(range, today);

  if (failed) {
    return (
      <div role="alert" className={notice}>
        <p className="font-bold text-foreground">Today could not be loaded.</p>
        <p className="text-sm text-muted-foreground">Open the page again in a moment.</p>
      </div>
    );
  }
  if (!overview) return <p role="status" className="text-sm text-muted-foreground">Adding everything up…</p>;

  return (
    <div className="space-y-gutter-lg">
      <TodayMoneyLine />
      <TodayFigures overview={overview} />

      <section className="space-y-stack-sm" aria-labelledby="today-work">
        <SectionLabel>
          <span id="today-work">What to do</span>
        </SectionLabel>
        <ReadyToSendCard />
        <RemindersCard />
        <div className="grid grid-cols-1 gap-stack-md md:grid-cols-3">
          <Box
            title="Do today"
            caption="Follow-ups, money to chase, unpaid deposits and care plans ending, most urgent first. Open one to act."
            className="md:col-span-2"
          >
            <AttentionList
              fill
              items={overview.attention.map((a) => ({ ...a, to: a.href }))}
              label="Needs attention"
              empty="Nothing needs you right now."
            />
          </Box>
          <Box title="Open pipeline by stage" caption="Where your open deals sit, and what they are worth.">
            <BarList items={overview.pipeline} label="Open pipeline by stage" />
          </Box>
        </div>
      </section>

      <section className="space-y-stack-sm" aria-labelledby="today-dates">
        <div className="flex flex-wrap items-center justify-between gap-stack-sm">
          <SectionLabel>
            <span id="today-dates">Quotes and clients in these dates</span>
          </SectionLabel>
          {picker}
        </div>
        <div className="grid grid-cols-1 gap-stack-md md:grid-cols-2">
          <Box title="Quotes in these dates" caption="How many quotes became work, and what winning a client costs.">
            <BarList items={overview.quotes} label="Quotes in these dates" />
          </Box>
          <Box title="Margin and hours per client" caption="Which clients pay for your time. Margin is what they paid less your time and costs.">
            {overview.clients.length === 0 ? (
              <p className={empty}>
                No client income or time in these dates. Log time from a client's page to see it here.
              </p>
            ) : (
              <BarList items={overview.clients} label="Margin and hours per client" />
            )}
          </Box>
        </div>
      </section>
    </div>
  );
};

/** The one main button: start something new. */
const NewMenu: React.FC<{ onNewLead: () => void }> = ({ onNewLead }) => {
  const { items } = useNewMenuController(onNewLead);
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button type="button" className="h-11 rounded-none">
          <Plus className="mr-stack-xs h-4 w-4" aria-hidden />
          New
          <ChevronDown className="ml-stack-xs h-4 w-4" aria-hidden />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-72 rounded-none border-border bg-card">
        {items.map((item) => (
          <DropdownMenuItem key={item.label} className="min-h-11 flex-col items-start gap-stack-xs" onSelect={item.run}>
            <span className="font-bold">{item.label}</span>
            <span className="text-xs text-muted-foreground">{item.hint}</span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

/** `tabs` is the Business / Website switch; it sits under the title. */
export const TodayPage: React.FC<{ tabs?: React.ReactNode }> = ({ tabs }) => {
  const { today, range, presets, setRange, adding, setAdding, openAddLead } = useTodayPageController();

  const picker = (
    <DateRangeSelect
      range={range}
      today={today}
      booksStart={BOOKS_START}
      yearEndMonth={DEFAULT_YEAR_END_MONTH}
      presetRange={presets}
      onChange={setRange}
    />
  );

  return (
    <>
      <PageHeader title="Today">
        <NewMenu onNewLead={openAddLead} />
      </PageHeader>
      {tabs}
      {/* Keyed on the dates so a new range loads its own time and costs. */}
      <TodayBody key={`${range.from}:${range.to}`} range={range} today={today} picker={picker} />
      <AddLeadDialog open={adding} onOpenChange={setAdding} />
    </>
  );
};
